import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft, Loader2, Save, Search, Trash2, UserPlus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/teams/$teamId')({
  component: TeamDetailPage,
})

interface Member {
  userId: string
  name: string
  email: string
  joinedAt: Date
}

interface UserRow {
  id: string
  name: string
  email: string
}

function TeamDetailPage() {
  const t = useTranslations()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { teamId } = Route.useParams()

  const teamQ = useQuery(orpc.rbac.teams.get.queryOptions({ input: { id: teamId } }))
  const usersQ = useQuery(orpc.rbac.teams.users.list.queryOptions())

  const team = teamQ.data as
    | {
        id: string
        name: string
        description: string | null
        memberCount: number
        members: Member[]
      }
    | undefined
  const users = (usersQ.data ?? []) as UserRow[]

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [picker, setPicker] = useState('')
  const [removeDialog, setRemoveDialog] = useState<{
    open: boolean
    userId: string | null
    name: string
  }>({ open: false, userId: null, name: '' })

  // Sync form with fetched team data on first load / after refetch.
  // Tracked via a "loaded" guard so editing isn't clobbered by a stale
  // refetch that races with the user's keystrokes.
  const [loaded, setLoaded] = useState(false)
  if (team && !loaded) {
    setName(team.name)
    setDescription(team.description ?? '')
    setLoaded(true)
  }

  const memberIds = useMemo(() => new Set(team?.members.map((m) => m.userId) ?? []), [team])
  const candidateUsers = useMemo(() => {
    const q = picker.trim().toLowerCase()
    return users
      .filter((u) => !memberIds.has(u.id))
      .filter((u) =>
        q === '' ? true : u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
      )
  }, [users, memberIds, picker])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: orpc.rbac.teams.get.queryKey({ input: { id: teamId } }) })
    qc.invalidateQueries({ queryKey: orpc.rbac.teams.list.key() })
  }

  const updateMut = useMutation(
    orpc.rbac.teams.update.mutationOptions({
      onSuccess: () => {
        toast.success(t('rbac.teams.updated') || 'Team updated')
        invalidate()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const addMut = useMutation(
    orpc.rbac.teams.members.add.mutationOptions({
      onSuccess: () => {
        toast.success(t('rbac.teams.memberAdded') || 'Member added')
        invalidate()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const removeMut = useMutation(
    orpc.rbac.teams.members.remove.mutationOptions({
      onSuccess: () => {
        toast.success(t('rbac.teams.memberRemoved') || 'Member removed')
        setRemoveDialog({ open: false, userId: null, name: '' })
        invalidate()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  if (teamQ.isLoading) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-4xl space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (teamQ.isError || !team) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-4xl">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">
              {t('rbac.teams.notFound') || 'Team not found'}
            </p>
            <Button variant="outline" onClick={() => navigate({ to: '/settings/teams' })}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('rbac.teams.backToList') || 'Back to teams'}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const dirty = name !== team.name || (description || null) !== (team.description ?? '')

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
          <Link to="/settings/teams">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" />
            {t('rbac.teams.backToList') || 'Back to teams'}
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">{team.name}</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('rbac.teams.settings') || 'Team settings'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="team-name">{t('rbac.teams.name') || 'Name'}</Label>
            <Input
              id="team-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={128}
            />
          </div>
          <div>
            <Label htmlFor="team-desc">{t('rbac.teams.description') || 'Description'}</Label>
            <Textarea
              id="team-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={3}
            />
          </div>
          <div className="flex justify-end">
            <Button
              disabled={!dirty || name.trim().length === 0 || updateMut.isPending}
              onClick={() =>
                updateMut.mutate({
                  id: teamId,
                  name: name.trim(),
                  description: description.trim() === '' ? null : description.trim(),
                })
              }
            >
              {updateMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {t('common.save') || 'Save'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">
            {t('rbac.teams.members') || 'Members'}{' '}
            <Badge variant="secondary" className="ml-2">
              {team.memberCount}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="user-picker" className="mb-1 block">
              {t('rbac.teams.addMember') || 'Add member'}
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                id="user-picker"
                value={picker}
                onChange={(e) => setPicker(e.target.value)}
                placeholder={t('rbac.teams.searchUsers') || 'Search by name or email'}
                className="pl-9"
              />
            </div>
            {picker.trim() !== '' && (
              <div className="mt-2 border rounded-md max-h-60 overflow-auto bg-background">
                {candidateUsers.length === 0 ? (
                  <div className="p-3 text-sm text-muted-foreground">
                    {t('rbac.teams.noMatches') || 'No matching users'}
                  </div>
                ) : (
                  candidateUsers.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => {
                        addMut.mutate({ teamId, userId: u.id })
                        setPicker('')
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-muted/40 flex items-center justify-between gap-2 text-sm border-b last:border-b-0"
                    >
                      <span>
                        <span className="font-medium">{u.name || u.email}</span>
                        {u.name && u.email && (
                          <span className="text-muted-foreground ml-2">{u.email}</span>
                        )}
                      </span>
                      <UserPlus className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {team.members.length === 0 ? (
            <div className="py-6 text-center text-muted-foreground text-sm">
              {t('rbac.teams.noMembers') || 'No members yet'}
            </div>
          ) : (
            <div className="border rounded-md divide-y">
              {team.members.map((m) => (
                <div key={m.userId} className="px-4 py-2 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{m.name || m.email}</div>
                    {m.name && m.email && (
                      <div className="text-xs text-muted-foreground">{m.email}</div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setRemoveDialog({
                        open: true,
                        userId: m.userId,
                        name: m.name || m.email,
                      })
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={removeDialog.open}
        onOpenChange={(open) => setRemoveDialog((d) => ({ ...d, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('rbac.teams.removeMemberTitle') || 'Remove from team?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('rbac.teams.removeMemberBody') ||
                'The user loses visibility into records owned by other team members on any team-scoped grant.'}{' '}
              — {removeDialog.name}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              onClick={() =>
                removeDialog.userId && removeMut.mutate({ teamId, userId: removeDialog.userId })
              }
            >
              {t('rbac.teams.remove') || 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
